import {
    Button,
    Collapse,
    Divider,
    List,
    Text,
    ThemeIcon,
    Transition,
} from "@mantine/core";

import {Group} from "@mantine/core";
import {Box} from "@mantine/core";
import {
    AgentThought,
    KAIResponseType,
    MessageWithMetadata,
} from "../../../types";
import {useDisclosure} from "@mantine/hooks";
import IconChevronDown from "../../../assets/chevron-down.svg?react";
import IconChevronUp from "../../../assets/chevron-up.svg?react";
import classes from "./AssistantWorkBox.module.css";
import parentClasses from "./AssistantMessage.module.css";
import shimmer from "../shimmerText.module.css";
import {useTranslation} from "react-i18next";
import {useEffect, useState} from "react";
import IconSearch from "../../../assets/search.svg?react";
import {GPTStreamParser} from "../components/stream-parser/GPTStreamParser";
import {useAppSelector} from "../../../app/hooks";
type AssistantWorkBoxProps = {
    message: MessageWithMetadata;
    feedback?: boolean;
};

export const AssistantWorkBox = ({
    message,
    feedback,
}: AssistantWorkBoxProps) => {
    const {t} = useTranslation();
    const haveAgentsStarted = message.activities?.length > 0;
    const hasThoughts = message.thoughts?.length > 0;
    const hasPluginActivities = (message.pluginActivities?.length ?? 0) > 0;
    const hasListItems = hasThoughts || hasPluginActivities;
    const isReflectingFinished = message.content.length > 0;
    const [thoughtsOpened, {toggle: toggleThoughts, close: closeThoughts}] =
        useDisclosure(!isReflectingFinished);
    const [hasBeenManuallyOpened, setHasBeenManuallyOpened] = useState(false);

    useEffect(() => {
        if (isReflectingFinished) {
            setTimeout(() => {
                closeThoughts();
            }, 600);
        }
    }, [isReflectingFinished]);

    const thoughts = message.thoughts ?? [];
    const pluginActivities = message.pluginActivities ?? [];
    const listItems = [
        ...thoughts.map((thought) => ({
            id: thought.sequenceNumber,
            type: "thought",
            content: thought.content,
        })),
        ...pluginActivities.map((activity) => ({
            id: activity.sequenceNumber,
            type: "search",
            content:
                "metadata" in activity &&
                "query" in activity.metadata &&
                typeof activity.metadata["query"] === "string"
                    ? activity.metadata["query"]
                    : "",
        })),
    ];

    if (
        (isReflectingFinished && !hasListItems) ||
        (!hasListItems && message.responseType === KAIResponseType.Fast)
    ) {
        return null;
    }

    return (
        <Box mb={"md"} style={{marginTop: -8}}>
            <Group w={"100%"} justify="flex-start">
                <Button
                    variant="transparent"
                    styles={{
                        inner: {
                            justifyContent: "flex-start",
                        },
                        label: {
                            flex: 1,
                        },
                    }}
                    size="md"
                    w={"100%"}
                    px={0}
                    onClick={() => {
                        if (isReflectingFinished && hasListItems) {
                            toggleThoughts();
                            if (!thoughtsOpened) {
                                setHasBeenManuallyOpened(true);
                            }
                        }
                    }}
                    color={"dark"}
                >
                    <Group gap={3} wrap="nowrap">
                        <Text
                            className={
                                !isReflectingFinished
                                    ? `${shimmer.shimmerText} ${parentClasses.thinkingGradient}`
                                    : ""
                            }
                        >
                            {t(
                                isReflectingFinished
                                    ? "assistant.answerHeading"
                                    : "reflections.button.text.loading"
                            )}
                        </Text>
                        {thoughtsOpened ? (
                            <IconChevronUp className={classes.thoughtsIcon} />
                        ) : (
                            <IconChevronDown className={classes.thoughtsIcon} />
                        )}
                    </Group>
                </Button>
            </Group>

            <Collapse in={isReflectingFinished ? thoughtsOpened : true}>
                <List pl={"xs"} withPadding spacing="xs" listStyleType="none">
                    {(hasBeenManuallyOpened
                        ? listItems?.sort((a, b) => a.id - b.id)
                        : listItems?.length > 0
                          ? [
                                listItems.sort((a, b) => a.id - b.id)[
                                    listItems.length - 1
                                ],
                            ]
                          : []
                    )?.map((item) => (
                        <AnimatedInListItem
                            key={item.id}
                            item={item}
                            initialVisible={hasBeenManuallyOpened}
                        />
                    ))}
                </List>
                {isReflectingFinished && hasListItems && <Divider my={"lg"} />}
            </Collapse>
        </Box>
    );
};

const AnimatedInListItem = ({
    item,
    initialVisible,
}: {
    item: {id: number; content: string; type: "thought" | "search"};
    initialVisible: boolean;
}) => {
    const [isVisible, setIsVisible] = useState(initialVisible);
    useEffect(() => {
        if (!initialVisible) {
            setTimeout(() => {
                setIsVisible(true);
            }, 100);
        }
    }, [initialVisible]);
    return (
        <Transition transition="pop" duration={200} mounted={isVisible}>
            {(styles) => (
                <>
                    {item.type === "search" ? (
                        <Group
                            key={item.id}
                            style={styles}
                            className={classes.searchItem}
                            gap={0}
                            wrap="nowrap"
                        >
                            <IconSearch
                                style={{
                                    width: "16px",
                                    height: "16px",
                                    color: "var(--mantine-color-gneis-9)",
                                    flexShrink: 0,
                                }}
                            />
                            <Text c="gneis.9" ml={"xs"}>
                                {item.content}
                            </Text>
                        </Group>
                    ) : (
                        <List.Item
                            key={item.id}
                            style={{
                                ...styles,
                                borderRadius: "var(--mantine-radius-md)",
                            }}
                            bg="gneis.1"
                            p="md"
                        >
                            <Text span c="gneis.9">
                                <GPTStreamParser
                                    text={item.content}
                                    className={classes.gptStreamParserContent}
                                    delta={10}
                                    options={{
                                        chunkSize: 3,
                                    }}
                                />
                            </Text>
                            {/* <Text ml={"xs"}>{item.content}</Text> */}
                        </List.Item>
                    )}
                </>
            )}
        </Transition>
    );
};
